import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Observable, of, BehaviorSubject, Subscription } from "rxjs";
import { tap, switchMap } from "rxjs/operators";
//import { GridOptions, ColDef } from '@ag-grid-enterprise/all-modules';4
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { GridFormatterService } from '../../../../../common/services/grid-formatter.service';
import { FinancialAssuranceService } from '../../../../../common/services/financial-assurance.service';
import { ModalInfoComponent } from "../../../../../common/components/ag-grid/modal-info.component";
import { Dialog } from "../../../../../core/models/dialog.model";
import { StratigraphicWellFinancialAssuranceView, StratigraphicWellFinancialAssuranceRow } from "../../../../../common/models/financialAssurance.model";

@Component({
    selector: 'stratigraphic-wells-requiring-bonding',
    templateUrl: './stratigraphic-wells-requiring-bonding.component.html',
    styleUrls: ['./stratigraphic-wells-requiring-bonding.component.scss'],
    standalone: false
})
export class StratigraphicWellsRequiringBondingComponent implements OnInit, OnDestroy {

    private _data: StratigraphicWellFinancialAssuranceView[];
    private _data$: BehaviorSubject<StratigraphicWellFinancialAssuranceView[]> = new BehaviorSubject<StratigraphicWellFinancialAssuranceView[]>(null);
    get data(): StratigraphicWellFinancialAssuranceView[] {
        return this._data;
    }

    @Input() gridAriaLabel: string;
    @Input() set data(value: StratigraphicWellFinancialAssuranceView[]) {
        this._data = value;
        this._data$.next(this._data);
    }

    dataObservable$: Observable<StratigraphicWellFinancialAssuranceView[]> = this.financialAssuranceService.TypeDescriptions.pipe(
        tap((data) => {
            this.typeDefinitions = data;
        }),
        switchMap(() => {
            return this._data$.asObservable();
        })
    )
    dialogText: string = "Bond Details for API #[XXX]";
    detailDialog: Dialog = new Dialog("", []);
    detailGridOptions: GridOptions;
    excelStyles: any = this.formatter.excelStyles;

    gridOptions: GridOptions;
	gridApi?: GridApi;

    subscription: Subscription = new Subscription();

    typeDefinitions: any[];

    constructor(private formatter: GridFormatterService, private financialAssuranceService: FinancialAssuranceService) { }

    ngOnInit() {
        this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildColumns(), false, true, true, false);
        this.gridOptions.headerHeight = 50;
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.detailGridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(this.buildDetailGrid(), false, true, true, false);
        this.detailGridOptions.headerHeight = 50;    
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    buildColumns(): ColDef[] {
        return [
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "API #",
                field: "apiNumber",
                tooltipField: "apiNumber",
                headerTooltip: "API #",
                maxWidth: 115,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Company Name",
                field: "companyName",
                tooltipField: "companyName",
                headerTooltip: "Company Name",
                maxWidth: 150,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Organization",
                field: "organizationId",
                tooltipField: "organizationId",
                headerTooltip: "Organization",
                maxWidth: 175,
                minWidth: 150,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "RBDMS #",
                field: "rbdmsOwnerNumber",
                tooltipField: "rbdmsOwnerNumber",
                headerTooltip: "RBDMS #",
                maxWidth: 150,
                minWidth: 135,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Owner Type",
                field: "ownerType",
                tooltipField: "ownerType",
                headerTooltip: "Owner Type",
                maxWidth: 150,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Date Spudded",
                field: "spudDate",
                tooltipValueGetter: this.formatter.formatDate("spudDate", "MM/dd/yyyy", "N/A"),
                valueFormatter: this.formatter.formatDate("spudDate", "MM/dd/yyyy", "N/A"),
                headerTooltip: "Date Spudded",
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                maxWidth: 125
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Bond Type",
                field: "bondType",
                headerTooltip: "Bond Type",
                tooltipValueGetter: this.formatter.formatPotentiallyBlankField("bondType", "N/A"),
                valueFormatter: this.formatter.formatPotentiallyBlankField("bondType", "N/A"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 125
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Compliance",
                field: "inCompliance",
                headerTooltip: "Compliance",
                tooltipValueGetter: this.formatter.formatBooleanToYesNo("inCompliance"),
                valueGetter: this.formatter.formatBooleanToYesNo("inCompliance"),
                maxWidth: 145,
                minWidth: 140,
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Bonding Required",
                field: "bondingRequired",
                headerTooltip: "Bonding Required",
                tooltipValueGetter: this.formatter.formatCurrency("bondingRequired"),
                valueFormatter: this.formatter.formatCurrency("bondingRequired"),
                maxWidth: 150,
                minWidth: 150,
                filter: "agNumberColumnFilter",
                filterParams: this.formatter.defaultNumberFilterParams
            },
            {
                headerClass: "wrapped",
                headerName: "Bonding Provided",
                field: "bondingPrrovided",
                headerTooltip: "Bonding Provided",
                tooltipValueGetter: this.formatter.formatCurrency("bondingProvided"),
                valueFormatter: this.formatter.formatCurrency("bondingProvided"),
                maxWidth: 125,
                minWidth: 125,
                filter: "agNumberColumnFilter",
                filterParams: this.formatter.defaultNumberFilterParams
            },
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Bonding Required By",
                field: "bondingRequiredBy",
                tooltipValueGetter: this.formatter.formatDate("bondingRequiredBy", "MM/dd/yyyy", "N/A"),
                valueFormatter: this.formatter.formatDate("bondingRequiredBy", "MM/dd/yyyy", "N/A"),
                headerTooltip: "Bonding Required By",
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                minWidth: 150,
                maxWidth: 165
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Bonding Expiration Date",
                field: "bondExpirationDate",
                tooltipValueGetter: this.formatter.formatDate("bondExpirationDate", "MM/dd/yyyy", "N/A"),
                valueFormatter: this.formatter.formatDate("bondExpirationDate", "MM/dd/yyyy", "N/A"),
                headerTooltip: "Bonding Expiration Date",
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                maxWidth: 165,
                minWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                cellRendererSelector: (params) => {
                    return {
                        component: ModalInfoComponent,
                        params: {
                            text: (data: any) => { return "Bond Details"; },
                            showDetailButton: true,
                            dialog: this.getDetailDialog()
                        }
                    }
                },
                minWidth: 120,
                maxWidth: 120
            } as ColDef
        ] as ColDef[];
    }

    buildDetailGrid(): ColDef[] {
        return [
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "API #",
                field: "apiNumber",
                tooltipField: "apiNumber",
                headerTooltip: "API #",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                minWidth: 115,
                maxWidth: 115
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Company Name",
                field: "companyName",
                tooltipField: "companyName",
                headerTooltip: "Company Name",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 150,
                minWidth: 150
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Organization",
                field: "organizationId",
                tooltipField: "organizationId",
                headerTooltip: "Organization",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 165,
                minWidth: 160
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "RBDMS #",
                field: "rbdmsOwnerNumber",
                tooltipField: "rbdmsOwnerNumber",
                headerTooltip: "RBDMS #",
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultTextFilterParams,
                maxWidth: 145,
                minWidth: 135
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Compliance",
                field: "inCompliance",
                headerTooltip: "Compliance",
                tooltipValueGetter: this.formatter.formatBooleanToYesNo("inCompliance"),
                valueGetter: this.formatter.formatBooleanToYesNo("inCompliance"),
                maxWidth: 175,
                minWidth: 150
            } as ColDef,
            {
                headerClass: "wrapped",
                headerName: "Bonding Required",
                field: "bondingRequired",
                headerTooltip: "Bonding Required",
                tooltipValueGetter: this.formatter.formatCurrency("bondingRequired"),
                valueFormatter: this.formatter.formatCurrency("bondingRequired"),
                maxWidth: 200,
                minWidth: 175
            },
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Bond Type",
                field: "instrumentTypeDescription",
                headerTooltip: "Bond Type",
                tooltipValueGetter: this.formatter.formatPotentiallyBlankField("instrumentTypeDescription", "N/A"),
                valueFormatter: this.formatter.formatPotentiallyBlankField("instrumentTypeDescription", "N/A"),
                filter: "agTextColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                maxWidth: 200,
                minWidth: 125
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Bonding Required By",
                field: "bondingRequiredBy",
                tooltipValueGetter: this.formatter.formatDate("bondingRequiredBy", "MM/dd/yyyy", "N/A"),
                valueFormatter: this.formatter.formatDate("bondingRequiredBy", "MM/dd/yyyy", "N/A"),
                headerTooltip: "Bonding Required By",
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                maxWidth: 175,
                minWidth: 150
            } as ColDef,
            {
                cellClass: "stringType",
                headerClass: "wrapped",
                headerName: "Bonding Expiration Date",
                field: "bondExpirationDate",
                tooltipValueGetter: this.formatter.formatDate("instrumentExpirationDate", "MM/dd/yyyy", "N/A"),
                valueFormatter: this.formatter.formatDate("instrumentExpirationDate", "MM/dd/yyyy", "N/A"),
                headerTooltip: "Bonding Expiration Date",
                filter: "agDateColumnFilter",
                filterParams: this.formatter.defaultDateFilterParams,
                maxWidth: 175,
                minWidth: 150
            } as ColDef
        ] as ColDef[];
    }

    getDetailDialog(): any {
        return (obj) => {
            let dialog = this.detailDialog;
            let rows: StratigraphicWellFinancialAssuranceRow[] = [];
            if (obj != null) {
                if (obj["apiNumber"] != null) dialog.message = this.dialogText.replace("[XXX]", obj["apiNumber"]);
                if (obj["rows"] != null) rows = obj["rows"];
            }
            dialog.setTabulation(this.detailGridOptions, rows);
            return dialog;
        }
    }
}

